import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { getSessionKeys } from '../../../services/chat/chatKeys';
import {
  PASSKEY_ALREADY_ON_DEVICE_MESSAGE,
  PASSKEY_UNSUPPORTED_MESSAGE,
  PasskeyError,
  addPasskey,
  defaultPasskeyName,
} from '../../../services/chat/chatPasskeys';
import type { PasskeyInfo } from '../../../services/chat/chatService';
import { SettingsGateCancelled, isReauthRequired, useSettingsGate } from './useSettingsGate';

export const MAX_PASSKEYS_MESSAGE = 'You already have the maximum of 5 passkeys. Remove one to add another.';

export interface AddPasskeyDialogProps {
  open: boolean;
  uid: string;
  email: string;
  /** The stored `prfSalt` (standard base64) from GET /keys/me. */
  prfSalt: string;
  passkeyCount: number;
  title?: string;
  intro?: string;
  /** `added` is the new passkey, or null when the dialog closed without adding one. */
  onClose: (added: PasskeyInfo | null) => void;
}

/**
 * "Add a passkey" (chat settings and the once-per-browser offer after a
 * recovery-key unlock). The settings gate — a recent assertion, an assertion,
 * or the fresh sign-in step — runs BEFORE `register/options`, because an
 * assertion replaces the single pending challenge; a 403 re-runs the gate,
 * `register/options`, and `create()` rather than retrying the same body.
 */
export function AddPasskeyDialog({
  open,
  uid,
  email,
  prfSalt,
  passkeyCount,
  title = 'Add a passkey',
  intro = 'A passkey unlocks chat on this device with a touch. It is synced by your Apple or Google account.',
  onClose,
}: AddPasskeyDialogProps) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gate = useSettingsGate({ uid, email, passkeyCount });

  useEffect(() => {
    if (open) {
      setName(defaultPasskeyName());
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const attempt = async (force: boolean): Promise<PasskeyInfo> => {
    await gate.ensureGate({ force });
    const session = getSessionKeys();
    if (!session) throw new Error('Chat is locked. Unlock it before adding a passkey.');
    return addPasskey({ prfSalt, privateKey: session.privateKey, name: name.trim() || defaultPasskeyName() });
  };

  const handleAdd = async () => {
    setBusy(true);
    setError(null);
    try {
      let added: PasskeyInfo;
      try {
        added = await attempt(false);
      } catch (err) {
        if (!isReauthRequired(err)) throw err;
        added = await attempt(true);
      }
      onClose(added);
    } catch (err: any) {
      if (err instanceof SettingsGateCancelled) {
        setError(null);
      } else if (err instanceof PasskeyError) {
        if (err.reason === 'already-registered') setError(PASSKEY_ALREADY_ON_DEVICE_MESSAGE);
        else if (err.reason === 'prf-unavailable') setError(PASSKEY_UNSUPPORTED_MESSAGE);
        else setError(err.message);
      } else if (err?.status === 409) {
        setError(MAX_PASSKEYS_MESSAGE);
      } else {
        setError(err?.message || 'Could not add the passkey. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : () => onClose(null)}
      maxWidth="xs"
      fullWidth
      className="ph-no-capture" PaperProps={{ className: 'ph-no-capture' }}
      aria-labelledby="add-passkey-title"
    >
      <DialogTitle id="add-passkey-title" sx={{ fontWeight: 600 }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {gate.signInStep ?? (
          <>
            <DialogContentText sx={{ fontSize: '0.875rem' }}>{intro}</DialogContentText>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Passkey name"
              value={name}
              onChange={(event) => setName(event.target.value.slice(0, 64))}
              size="small"
              fullWidth
              disabled={busy}
              inputProps={{ maxLength: 64 }}
            />
          </>
        )}
      </DialogContent>
      {!gate.signInStep && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => onClose(null)} disabled={busy} sx={{ color: 'text.secondary' }}>
            Not now
          </Button>
          <Button variant="contained" onClick={handleAdd} disabled={busy} sx={{ minWidth: 140 }}>
            {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Add passkey'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
